// utils/rutValidator.js
import { useState } from 'react';
/**
 * Limpia el RUT removiendo puntos y guiones
 * @param {string} rut - RUT a limpiar
 * @returns {string} - RUT limpio
 */
export const limpiarRut = (rut) => {
  return rut.replace(/[.-]/g, '').toUpperCase();
};

/**
 * Formatea el RUT con puntos y guión
 * @param {string} rut - RUT a formatear
 * @returns {string} - RUT formateado (ej: 19.140.775-K)
 */
export const formatearRut = (rut) => {
  // Limpiar el RUT
  const rutLimpio = limpiarRut(rut);
  
  if (rutLimpio.length < 2) {
    return rutLimpio;
  }
  
  // Separar cuerpo y dígito verificador
  const cuerpo = rutLimpio.slice(0, -1);
  const dv = rutLimpio.slice(-1);
  
  // Formatear el cuerpo con puntos
  const cuerpoFormateado = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${cuerpoFormateado}-${dv}`;
};

/**
 * Calcula el dígito verificador de un RUT
 * @param {string} rut - RUT sin dígito verificador
 * @returns {string} - Dígito verificador (0-9 o K)
 */
export const calcularDV = (rut) => {
  // Limpiar y obtener solo números
  const rutLimpio = limpiarRut(rut).replace(/[^0-9]/g, '');
  
  let suma = 0;
  let multiplicador = 2;
  
  // Recorrer el RUT de derecha a izquierda
  for (let i = rutLimpio.length - 1; i >= 0; i--) {
    suma += parseInt(rutLimpio.charAt(i)) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  
  const resto = suma % 11;
  const dv = 11 - resto;
  
  if (dv === 11) return '0';
  if (dv === 10) return 'K';
  return dv.toString();
};

/**
 * Valida si un RUT chileno es válido
 * @param {string} rut - RUT a validar (puede tener puntos y guión)
 * @returns {boolean} - true si es válido, false si no
 */
export const validarRut = (rut) => {
  // Verificar que no esté vacío
  if (!rut || rut.trim() === '') {
    return false;
  }
  
  // Limpiar el RUT
  const rutLimpio = limpiarRut(rut);
  
  // Verificar formato básico (mínimo 8 caracteres: 7 números + 1 dígito verificador)
  if (rutLimpio.length < 8 || rutLimpio.length > 9) {
    return false;
  }
  
  // Verificar que solo contenga números y opcionalmente K al final
  if (!/^[0-9]+[0-9K]$/.test(rutLimpio)) {
    return false;
  }
  
  // Separar cuerpo y dígito verificador
  const cuerpo = rutLimpio.slice(0, -1);
  const dvIngresado = rutLimpio.slice(-1);
  
  // Calcular el dígito verificador correcto
  const dvCalculado = calcularDV(cuerpo);
  
  // Comparar
  return dvIngresado === dvCalculado;
};

/**
 * Valida RUT y retorna objeto con detalles
 * @param {string} rut - RUT a validar
 * @returns {object} - {valido: boolean, mensaje: string, rutFormateado: string}
 */
export const validarRutDetallado = (rut) => {
  // Verificar que no esté vacío
  if (!rut || rut.trim() === '') {
    return {
      valido: false,
      mensaje: 'El RUT no puede estar vacío',
      rutFormateado: ''
    };
  }
  
  // Limpiar el RUT
  const rutLimpio = limpiarRut(rut);
  
  // Verificar longitud
  if (rutLimpio.length < 8) {
    return {
      valido: false,
      mensaje: 'El RUT es demasiado corto',
      rutFormateado: ''
    };
  }
  
  if (rutLimpio.length > 9) {
    return {
      valido: false,
      mensaje: 'El RUT es demasiado largo',
      rutFormateado: ''
    };
  }
  
  // Verificar formato
  if (!/^[0-9]+[0-9K]$/.test(rutLimpio)) {
    return {
      valido: false,
      mensaje: 'El RUT contiene caracteres inválidos',
      rutFormateado: ''
    };
  }
  
  // Separar cuerpo y dígito verificador
  const cuerpo = rutLimpio.slice(0, -1);
  const dvIngresado = rutLimpio.slice(-1);
  
  // Calcular el dígito verificador correcto
  const dvCalculado = calcularDV(cuerpo);
  
  // Validar
  if (dvIngresado !== dvCalculado) {
    return {
      valido: false,
      mensaje: `El dígito verificador es incorrecto. Debería ser ${dvCalculado}`,
      rutFormateado: formatearRut(rutLimpio)
    };
  }
  
  return {
    valido: true,
    mensaje: 'RUT válido',
    rutFormateado: formatearRut(rutLimpio)
  };
};


export const useRutInput = (initialValue = '') => {
  const [value, setValue] = useState(initialValue);
  const [formatted, setFormatted] = useState(initialValue);
  const [isValid, setIsValid] = useState(null);
  const [error, setError] = useState('');
  
  const handleChange = (e) => {
    const input = e.target.value;
    
    // Limpiar y limitar a números, K y caracteres de formato
    const cleaned = input.replace(/[^0-9kK.-]/g, '').toUpperCase();
    
    setValue(cleaned);
    setFormatted(cleaned);
    
    // Limpiar error mientras escribe
    if (error) setError('');
    if (isValid !== null) setIsValid(null);
  };
  
  const handleBlur = () => {
    if (!value) {
      setIsValid(false);
      setError('');
      return;
    }
    
    const validacion = validarRutDetallado(value);
    setIsValid(validacion.valido);
    setError(validacion.valido ? '' : validacion.mensaje);
    
    // Formatear automáticamente si es válido
    if (validacion.valido) {
      setFormatted(validacion.rutFormateado);
    }
  };
  
  const reset = () => {
    setValue('');
    setFormatted('');
    setIsValid(null);
    setError('');
  };
  
  return {
    value,
    formatted,
    isValid,
    error,
    onChange: handleChange,
    onBlur: handleBlur,
    reset
  };
};